<template>
  <div id="search-bar" class="flex items-center w-full relative">
    <div :key="`search-${searchIncrement}`" class="input-search w-full">
      <ClientOnly>
        <ComboboxRoot v-model="selected" as="div" :highlight-on-hover="false" multiple>
          <div class="relative">
            <div
              class="flex relative w-full cursor-default overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm h-[48px] justify-end"
              :class="noOverlay ? '' : 'z-50'"
            >
              <ComboboxInput
                id="input"
                class="input-search border-search-bar w-full px-4 py-3 rounded-lg md:rounded-r-none border focus:outline-none h-10.5 placeholder:text-gray-700"
                :class="{
                  'border-white': focused && !noOverlay,
                  'border-gray-400': !focused || noOverlay,
                  disabled: isDisabled,
                }"
                :disabled="isDisabled"
                placeholder="Rechercher un produit"
                :model-value="query"
                autocomplete="off"
                @update:model-value="query = $event"
                @focus="onFocus"
                @blur="onBlur"
                @keydown.enter="onSearch"
              />
              <ComboboxTrigger
                class="rounded-l-none rounded-r-lg h-10.5 button-search flex justify-center items-center text-sm cursor-pointer"
                :class="{ 'input-focused': focused }"
                @click="onSearch(true)"
              >
                {{ t('DjSearchBar.button') }}
              </ComboboxTrigger>
            </div>
            <div v-if="focused && !noOverlay" class="fixed inset-0 blured z-40" @click.stop />

            <Transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition ease-in duration-100"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <div v-show="openOptions">
                <ComboboxContent
                  id="options"
                  :class="{ 'z-50': noOverlay, 'h-sm': smallOptions }"
                  class="dj-searchbar-result absolute mt-1 w-full z-50"
                  static
                >
                  <div class="dj-searchbar-result-content w-full">
                    <div
                      class="dj-searchbar-products"
                      :class="{
                        'w-1/2': selectedProductId !== '',
                        'w-full': selectedProductId === '',
                      }"
                    >
                      <div
                        v-if="!loading && !productsList.length"
                        class="p-4 text-center text-gray-500"
                      >
                        {{ t('DjSearchBar.noData', { query: query }) }}
                      </div>
                      <div v-else class="dj-searchbar-products-title">
                        {{ t('DjSearchBar.products') }}
                      </div>
                      <div v-if="loading">
                        <ComboboxItem as="div" value="loading">
                          <li class="relative">
                            <span class="block">
                              <div class="dj-searchbar-products-list relative">
                                <div class="flex items-center space-x-4">
                                  <USkeleton class="h-12 w-12" :ui="{ rounded: 'rounded-full' }" />
                                  <div class="space-y-2">
                                    <USkeleton class="h-4 w-[250px]" />
                                    <USkeleton class="h-4 w-[200px]" />
                                  </div>
                                </div>
                              </div>
                            </span>
                          </li>
                        </ComboboxItem>
                      </div>
                      <div v-else class="dj-searchbar-result_list overflow-auto pr-3">
                        <div v-if="loading" class="space-y-4 p-4">
                          <div v-for="i in 3" :key="i" class="flex items-center space-x-4">
                            <USkeleton class="h-12 w-12" :ui="{ rounded: 'rounded-full' }" />
                            <div class="space-y-2">
                              <USkeleton class="h-4 w-[250px]" />
                              <USkeleton class="h-4 w-[200px]" />
                            </div>
                          </div>
                        </div>
                        <ComboboxItem
                          v-for="(product, index) in productsList"
                          v-else
                          :key="index"
                          as="template"
                          :value="product"
                        >
                          <li class="relative last:mb-2">
                            <span class="block">
                              <slot name="result" :row="product">
                                <div class="dj-searchbar-products-list relative">
                                  <DjProductSearchResult
                                    :product="product"
                                    :open="selectedProductId === product.product.externalId"
                                    :small="selectedProductId !== ''"
                                    :has-variants="hasVariants(product)"
                                    @select-product="selectProduct"
                                    @click="clickOption(product)"
                                  />
                                </div>
                              </slot>
                            </span>
                          </li>
                        </ComboboxItem>
                      </div>
                    </div>
                    <div v-if="selectedProductId !== '' && !hasVariants" class="w-1/2">
                      <div class="dj-searchbar-product-variants-title">
                        {{ t('DjSearchBar.references') }}
                      </div>
                      <li class="dj-searchbar-result_list relative overflow-auto pr-3">
                        <span
                          v-for="(variant, index) in variants"
                          :key="`reference-${index}-${increment}`"
                          class="block last:mb-2"
                        >
                          <div class="dj-searchbar-product-variants-list relative">
                            <DjProductSearchResultVariant :variant="variant" />
                          </div>
                        </span>
                      </li>
                    </div>
                  </div>
                </ComboboxContent>
              </div>
            </Transition>
          </div>
        </ComboboxRoot>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    ComboboxRoot,
    ComboboxInput,
    ComboboxContent,
    ComboboxItem,
    ComboboxTrigger,
  } from 'reka-ui';
  import { Transition } from 'vue';
  import { useProduct } from '@/composables/useProduct';
  import { AggregationType } from '@djust-b2b/djust-front-sdk/lib/services/product/definitions';
  import type { ProductVariantResponse } from '@djust-b2b/djust-front-sdk/lib/interfaces/models/product';

  const { t } = useI18n();

  const props = defineProps({
    buttonIcon: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    noSubmitBtn: {
      type: Boolean,
      default: false,
    },
    noVariant: {
      type: Boolean,
      default: false,
    },
    noOverlay: {
      type: Boolean,
      default: false,
    },
    smallOptions: {
      type: Boolean,
      default: false,
    },
    allwaysDisplaySearchBar: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['onSearch']);

  const { getProductVariants } = useProduct();
  const { autoCompleteSearchProducts } = useSearchProduct();
  const router = useRouter();
  const { locale } = useI18n();
  const searchIncrement = ref(0);
  const increment = ref(0);
  const searchButtonClicked = ref(false);
  const productsList = ref<ProductSearchContent[]>([]);
  const loading = ref(false);
  const { getUserCurrency } = useUser();
  const currency = computed(() => getUserCurrency());
  const focused = ref(false);
  const query = ref('');
  const selectedProductId = ref('');
  const variants = ref<ProductVariantResponse[]>([]);
  const selected = ref();
  const openOptions = ref(false);
  const displaySearchBar = ref<boolean>(true);
  const localePath = useLocalePath();
  const fetchProducts = async () => {
    try {
      loading.value = true;
      const autoCompleteParams: AutocompleteProductsRequest = {
        input: query.value,
        locale: locale.value,
        currency: currency.value,
        aggregation: AggregationType.VARIANT,
      };
      const searchProductsResponse = await autoCompleteSearchProducts(autoCompleteParams);
      productsList.value = searchProductsResponse.products;
    } catch (error) {
    } finally {
      loading.value = false;
    }
  };

  watch(query, async (newQuery: string) => {
    if (newQuery.length > 0) {
      await fetchProducts();
      openOptions.value = true;
    } else if (newQuery.length === 0) {
      openOptions.value = false;
      productsList.value = [];
    } else {
      productsList.value = [];
    }
  });

  onMounted(() => {
    updateDisplaySearchBar();
    window.addEventListener('resize', updateDisplaySearchBar);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateDisplaySearchBar);
  });

  const updateDisplaySearchBar = () => {
    displaySearchBar.value = window.innerWidth > 768;
  };

  const focusSearch = () => {
    displaySearchBar.value = true;
  };

  const hasVariants = (product: { attributes: any[] }) => {
    return (
      product.attributes.find(
        (attribute: { externalId: string }) => attribute.externalId === 'nb_variants'
      )?.value > 0 && !props.noVariant
    );
  };

  const onSearch = async (button: any) => {
    emit('onSearch');
    try {
      if (button) {
        searchButtonClicked.value = true;
      }
      await router.push(localePath({ path: '/search', query: { query: query.value } }));
    } catch (error) {
    } finally {
      closeOptions();
      searchButtonClicked.value = false;
      updateDisplaySearchBar();
    }
  };

  const onFocus = () => {
    emit('onSearch');
    if (!searchButtonClicked.value) {
      focused.value = true;
      openOptions.value = true;
    }
  };

  const onBlur = (event: { relatedTarget: any }) => {
    emit('onSearch');
    focused.value = false;
    selectedProductId.value = '';
    if (!event.relatedTarget) {
      query.value = '';
    }
    updateDisplaySearchBar();
  };

  const clickOption = (product: { product: Product }) => {
    router.push(localePath(`/product/${product.product.sku}`));
    closeOptions();
    searchIncrement.value++;
  };

  const closeOptions = () => {
    (document.querySelector('#input') as HTMLElement).blur();
    openOptions.value = false;
    query.value = '';
    updateDisplaySearchBar();
  };

  const selectProduct = async (product: { product: { externalId: string; sku: any } }) => {
    selectedProductId.value = product.product.externalId;
    const response = await getProductVariants({
      productSku: product.product.sku,
      locale,
      pageable: { page: 0, size: 999 },
    });
    variants.value = response?.variants?.content ?? [];
    increment.value++;
    updateDisplaySearchBar();
  };
</script>

<style lang="scss" scoped>
  @use './DjSearchBar.scss';

  li {
    list-style: none;
    &::marker {
      display: none;
    }
  }
</style>
